import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileDown, FileText, BookOpenCheck, FlaskConical, ScanSearch } from 'lucide-react';
import { FamilyStrip } from '@/components/spec/FamilyStrip';
import { Navbar, Footer } from '@/components/Layout';
import { CodeBlock } from '@/components/CodeBlock';
import spec from '@/lib/openConsentSpec.json';

const PDF = '/downloads/kojiru-open-consent-receipt-spec-draft-0.1.pdf';
const MD = '/downloads/kojiru-open-consent-receipt-spec-draft-0.1.md';
const VECTORS = '/downloads/kojiru-open-consent-receipt-test-vectors-draft-0.1.json';


function SpecSection({ s, i }) {
  const List = s.ordered ? 'ol' : 'ul';
  return (
    <motion.section id={s.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.03 * (i % 4) }} className="scroll-mt-28 border-t border-white/[0.06] pt-10" data-testid={`spec-section-${s.id}`}>
      <h2 className="text-base md:text-lg font-semibold text-white mb-4">{s.title}</h2>
      {(s.body || []).map((p) => <p key={p} className="text-sm md:text-base text-gray-400 leading-relaxed mb-4">{p}</p>)}
      {s.list && (
        <List className={`${s.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-2 text-sm text-gray-300 leading-relaxed mb-4`}>
          {s.list.map((t) => <li key={t}>{t}</li>)}
        </List>
      )}
      {s.code && <CodeBlock title="EIP-712 types" code={s.code} lang="text" testId="spec-typed-data-code" />}
    </motion.section>
  );
}

function Toc() {
  return (
    <nav className="hidden lg:block sticky top-28 self-start" data-testid="spec-toc">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Contents</div>
      <ul className="space-y-2 text-xs">
        {spec.sections.map((s) => (
          <li key={s.id}><a href={`#${s.id}`} className="text-gray-400 hover:text-white transition-colors" data-testid={`spec-toc-${s.id}`}>{s.title}</a></li>
        ))}
      </ul>
    </nav>
  );
}

function Downloads() {
  const cls = 'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors';
  return (
    <div className="flex flex-wrap gap-3 mt-8">
      <a href={PDF} target="_blank" rel="noopener noreferrer" className={`${cls} bg-white text-black hover:bg-gray-200`} data-testid="spec-download-pdf"><FileDown className="w-4 h-4" /> Download PDF</a>
      <a href={MD} target="_blank" rel="noopener noreferrer" className={`${cls} border border-white/15 text-white hover:border-white/40`} data-testid="spec-download-md"><FileText className="w-4 h-4" /> Plain text (Markdown)</a>
      <a href={VECTORS} target="_blank" rel="noopener noreferrer" className={`${cls} border border-white/15 text-white hover:border-white/40`} data-testid="spec-download-vectors"><FlaskConical className="w-4 h-4" /> Test vectors (JSON)</a>
      <Link to="/specs/consent-receipt/checker" className={`${cls} border border-sky-500/40 text-sky-200 hover:border-sky-400`} data-testid="spec-checker-link"><ScanSearch className="w-4 h-4" /> Browser-only checker</Link>
    </div>
  );
}

export default function OpenConsentReceiptSpec() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="open-consent-spec-page">
      <Helmet>
        <title>Open Consent Receipt Specification | Kojiru</title>
        <meta name="description" content={spec.summary} />
      </Helmet>
      <Navbar />
      <header className="pt-32 pb-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-500/30 bg-sky-500/10 text-xs uppercase tracking-widest text-sky-300 mb-5" data-testid="spec-status-badge">
              <BookOpenCheck className="w-3.5 h-3.5" /> Open spec · {spec.version} · Published {spec.published}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl mb-5" data-testid="spec-title">{spec.title}</h1>
            <p className="text-base md:text-lg text-gray-400 max-w-3xl leading-relaxed" data-testid="spec-summary">{spec.summary}</p>
            <div className="mt-6 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-gray-300 leading-relaxed" data-testid="spec-publisher-notice">
              Kojiru operates no service for this format. Nothing on this page collects a signature, checks a receipt, or takes a payment. This format is separate from the <Link to="/binding-gate" className="text-gray-100 underline hover:text-white" data-testid="spec-gate-link">Binding Action Gate</Link>, which is a proposed design and not in service.
            </div>
            <Downloads />
            <p className="mt-6 text-sm text-gray-400" data-testid="spec-see-also">See also: <Link to="/specs/claim-records" className="text-gray-100 underline hover:text-white" data-testid="spec-claim-records-link">Claim Records</Link>, for what happens after a purchase.</p>
            <FamilyStrip current="consent" />
          </motion.div>
        </div>
      </header>
      <main className="px-6 pb-24">
        <div className="container mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12">
          <Toc />
          <div className="max-w-3xl space-y-10">
            {spec.sections.map((s, i) => <SpecSection key={s.id} s={s} i={i} />)}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
